import {HttpClient} from '@angular/common/http';
import {APP_INITIALIZER, Injectable, Provider} from '@angular/core';
import {firstValueFrom, from, lastValueFrom, Observable, of} from 'rxjs';
import {catchError, concatMap, finalize, map} from 'rxjs/operators';
import {ConfigService} from '../config/config.service';
import {TokenStorageService} from '../local-storage/token-storage.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AppMessageService} from '../app-message/app-message.service';
import {Router} from '@angular/router';
import {AuthActions, AuthUser} from './store/auth.models';
import {IndexedDBService} from '../indexdb/indexeddb.service';
import {TwoFactorEnum} from '../../../pages/settings/enum/settings-type.enum';

export interface AccessData {
  token_type: 'Bearer';
  expires_in: number;
  access_token: string;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly hostUrl: string;
  private readonly headers = {};

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly messageService: AppMessageService,
    private readonly appMessageService: AppMessageService,
    private readonly router: Router,
    private readonly indexedDBService: IndexedDBService
  ) {
    this.hostUrl = this.configService.getAPIUrl();
    this.headers = {'Current-Lang': 'tr'};
  }

  login(email: string, password: string): Observable<AccessData> {
  return this.http
    .post<AccessData>(
      `${this.hostUrl}auth/login`,
      {email, password},
      {headers: this.headers}
    );
}

  checkUserAuthentication(email: string, password: string, recaptchaToken?: string) {
    return this.http
      .post<
        AccessData | { isVerification: true; verificationType: TwoFactorEnum }
      >(
        `${this.hostUrl}auth/check-auhentication`,
        {email, password, recaptchaToken},
        {headers: this.headers}
      )
      .pipe(
        concatMap((res) => {
          // Eğer isVerification varsa, direkt döndür
          if ('isVerification' in res) {
            return of(res);
          }
          if (!res.access_token) {
            return of(false);
          }
          // Tokenları kaydet
          this.tokenStorageService.saveAccessToken(res.access_token);
          this.tokenStorageService.saveRefreshToken(res.refresh_token);

          // Kullanıcı bilgilerini al
          return this.getAuthUser().pipe(
            concatMap((user) => {
              localStorage.setItem('user', JSON.stringify(user));
              // Yetkileri al ve işlemleri sırayla yürüt
              return from(this.getAuthActions()).pipe(
                map(() => {
                  this.messageService.successToast(
                    '200',
                    'Giriş başarılı, Hoşgeldiniz!',
                    20000,
                    'top-right'
                  );
                  return true;
                })
              );
            })
          );
        }),
        catchError((err) => {
          this.appMessageService.errorToast(
            err.error,
            err.message,
            20000,
            'top-right'
          );
          return of(''); // Hata olursa boş string döndür
        })
      );
  }

  sendVerificationOtp(body: {
    email: string;
    password: string;
    otpCode: string;
    verificationType: TwoFactorEnum | null;
  }) {
    return this.http
      .post<AccessData>(`${this.hostUrl}auth/verify-otp`, body)
      .pipe(
        concatMap((res) => {
          if (!res.access_token) {
            return of(false);
          }
          // Tokenları kaydet
          this.tokenStorageService.saveAccessToken(res.access_token);
          this.tokenStorageService.saveRefreshToken(res.refresh_token);

          // Kullanıcı bilgilerini al
          return this.getAuthUser().pipe(
            concatMap((user) => {
              localStorage.setItem('user', JSON.stringify(user));
              // Yetkileri al ve işlemleri sırayla yürüt
              return from(this.getAuthActions()).pipe(
                map(() => {
                  this.messageService.successToast(
                    '200',
                    'Giriş başarılı, Hoşgeldiniz!',
                    20000,
                    'top-right'
                  );
                  return true;
                })
              );
            })
          );
        }),
        catchError((err) => {
          this.appMessageService.errorToast(
            err.error,
            err.message,
            20000,
            'top-right'
          );
          return of(false); // Hata olursa boş string döndür
        })
      );
  }

  preForgetPassword(email: string): Observable<any> {
    return this.http.post<{ success: boolean }>(
      `${this.hostUrl}auth/pre-forgot-password`,
      {email}
    );
  }

  postForgetPassword(data: {
    email: string;
    password: string;
    passwordConfirm: string;
    otpCode: string
  }): Observable<any> {
    return this.http.post<{ success: boolean }>(
      `${this.hostUrl}auth/post-forgot-password`,
      data
    );
  }

  verifyForgetPassword(data: {
    email: string;
    otpCode: string
  }): Observable<any> {
    return this.http.post<{ success: boolean }>(
      `${this.hostUrl}auth/verify-forgot-password`,
      data
    );
  }


  async logout(): Promise<void> {
    const token = this.getToken();
    if (token) {
      localStorage.clear();
      sessionStorage.clear();

      this.router.navigate(['/auth/login']);
    }
    else {
      localStorage.clear();
      await this.router.navigate(['/auth/login']);
    }
  }

  async refreshToken(): Promise<AccessData> {
    const refreshToken = this.tokenStorageService.getRefreshToken();
    return await firstValueFrom(
      this.http.post<AccessData>(`${this.hostUrl}auth/refresh`, {
        refreshToken: refreshToken,
      })
    );
  }

  getAuthUser(): Observable<AuthUser> {
    return this.http.get<AuthUser>(`${this.hostUrl}user/me`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
  }

  async getAuthActions() {
    this.http
      .get<AuthActions>(`${this.hostUrl}user/get-actions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
      .subscribe(async (response) => {
        await this.indexedDBService.addBulkPermissions('authority', response);
      });
  }

  getToken() {
    return localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  /*private getUserDatas(data: any | null): string {
    const token = this.getToken();
    if (token === undefined || token === null) {
      return '';
    }
    const jwtHelper = new JwtHelperService();
    const decodedToken = jwtHelper.decodeToken(token);
    return decodedToken[data];
  }*/

  getUserData() {
    const token = this.getToken();
    let decodedToken;
    if (token === undefined || token === null) {
      return null;
    }
    const jwtHelper = new JwtHelperService();
    try {
      decodedToken = jwtHelper.decodeToken(token);
      const user = JSON.parse(localStorage.getItem('user') || '');
      if (user.id == decodedToken.id) {
        return user;
      }
    } catch {
      this.refreshToken();
    }
    return decodedToken;
  }

  getUsername() {
    return this.getUserData()?.username;
  }

  isSuperAdmin() {
    return Boolean(this.getUserData().isSuperAdmin);
  }

  getUserId() {
    return this.getUserData().id;
  }

  /* getUserEmail() {
     return this.getUserDatas('email');
   }

   getUserFullName() {
     return `${this.getUserDatas('firstName')} ${this.getUserDatas('lastName')}`;
   }*/

  async setActiveModule(module: any) {
    const user = this.getUserData();
    user.activeModule = module.value;
    console.log(user);

    localStorage.setItem('user', JSON.stringify(user));
    this.router.navigate([module.link]);
  }

  async deleteActiveModule() {
    const user = this.getUserData();
    delete user.activeModule;
    console.log(user);

    localStorage.setItem('user', JSON.stringify(user));
    this.router.navigate(['dashboard/operations']);
  }

  async isLoggedIn() {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    if (!token && !refreshToken) {
      return false;
    }

    const jwtHelper = new JwtHelperService();
    if (
      jwtHelper.isTokenExpired(token) &&
      jwtHelper.isTokenExpired(refreshToken)
    ) {
      return false;
    } else if (jwtHelper.isTokenExpired(token)) {
      try {
        const res = await this.refreshToken();
        this.tokenStorageService.saveAccessToken(res.access_token);
        this.tokenStorageService.saveRefreshToken(res.refresh_token);
        return true;
      } catch (error) {
        console.error(error)
        return false;
      }
    }
    return true;
  }
}

export const AuthServiceInitProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (authService: AuthService) => () => {
  },
  deps: [AuthService],
  multi: true,
};
